import { Grid } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { Svgs } from '../../../assets/svg';
import { DropdownMenu } from '../../../components';
import {
  getCustomRange,
  getLastMonthRange,
  getLastQuarterRange,
  getLastYearRange,
  TransactionsDateRange,
  DateRangeType,
} from '../../../data/rev-share/helpers/transactionsUtils';
import { toDateKey } from '../../../helpers/utilityFunctions';
import LabelDatePicker from '../../../components/LabelDatePicker';

interface DateRangeDropdownProps {
  dateRange: TransactionsDateRange;
  onDateRangeChange: (range: TransactionsDateRange) => void;
  today: Dayjs;
  loading?: boolean;
  disabled?: boolean;
}

const DateRangeDropdown: React.FC<DateRangeDropdownProps> = ({
  dateRange,
  onDateRangeChange,
  today,
  loading = false,
  disabled = false,
}) => {
  const isDisabled = loading || disabled;
  const isCustomRange = dateRange.rangeType === DateRangeType.Custom;

  const handleRangeTypeChange = (value: string) => {
    if (isDisabled || value === dateRange.rangeType) return;

    const rangeType = value as DateRangeType;
    if (rangeType === dateRange.rangeType) return;

    let newRange: TransactionsDateRange;

    switch (rangeType) {
      case DateRangeType.LastMonth:
        newRange = getLastMonthRange();
        break;
      case DateRangeType.LastQuarter:
        newRange = getLastQuarterRange();
        break;
      case DateRangeType.LastYear:
        newRange = getLastYearRange();
        break;
      case DateRangeType.Custom:
      default:
        newRange = getCustomRange();
        break;
    }

    onDateRangeChange(newRange);
  };

  const handleStartDateChange = (newDate: Dayjs | null) => {
    if (isDisabled || !newDate) return;

    onDateRangeChange({
      ...dateRange,
      startDate: toDateKey(newDate),
    });
  };

  const handleEndDateChange = (newDate: Dayjs | null) => {
    if (isDisabled || !newDate) return;

    onDateRangeChange({
      ...dateRange,
      endDate: toDateKey(newDate),
    });
  };

  return (
    <Grid display='flex' flexDirection='row' gap={2} alignItems='center'>
      <DropdownMenu
        variant='outlined'
        formControlProps={{ className: 'rev-share-select' }}
        options={Object.values(DateRangeType)}
        selected={dateRange.rangeType}
        callback={handleRangeTypeChange}
        disabled={isDisabled}
        icon={<Svgs.ActionSmallCalendarDefault />}
      />
      {isCustomRange && (
        <>
          <LabelDatePicker
            value={dateRange.startDate ? dayjs(dateRange.startDate) : null}
            onChange={handleStartDateChange}
            maxDate={today}
            disabled={isDisabled}
            placeholder='Choose'
            label='Start'
          />
          <LabelDatePicker
            value={dateRange.endDate ? dayjs(dateRange.endDate) : null}
            onChange={handleEndDateChange}
            minDate={dateRange.startDate ? dayjs(dateRange.startDate) : undefined}
            maxDate={today}
            disabled={isDisabled}
            placeholder='Choose'
            label='End'
          />
        </>
      )}
    </Grid>
  );
};

export default DateRangeDropdown;
